/**
 * @deprecated This entire file is deprecated. Use Themed/Text component instead.
 *
 * Migration Guide:
 * Before:
 *   import { H1, P1, P2 } from 'app/components/Typography';
 *   <H1>Title</H1>
 *   <P1>Text</P1>
 *
 * After:
 *   import { Text } from 'app/components/Themed/Text';
 *   <Text variant="h1">Title</Text>
 *   <Text variant="p1">Text</Text>
 *
 * Available variants:
 * - Headings: h1, h1semi, h1p5, h1p5semi, h2, h2semi, h2p5, h2p5semi, h3, h3semi, h4, h4semi, h5, h5semi, h6, h6semi, h7
 * - Paragraphs: p1, p1bold, p2, p2bold, p3, p3bold
 * - Links: p1Link, p2Link, a
 * - Other: bodySmall, caption, button, smallButton, limitLineText
 *
 * Additional features:
 * - Change element type: <Text variant="h1" as="div">
 * - Add custom classes: <Text variant="p1" className="custom-class">
 * - Line clamping: <Text variant="p1" lineClamp={2}>
 */

import styled, { css } from "styled-components";
import { color, fontSize, space } from "styled-system";
import Text from "app/components/Text";
import {
  FONT_SIZES,
  LETTER_SPACING,
  FONT_WEIGHTS,
  TEXT_TRANSFORM,
} from "./constants";

/**
 * Shared styles for typography components
 * @deprecated Use Themed/Text component instead
 */
const sharedStyles = css`
  ${color};
  ${space};
  ${fontSize};
`;

/**
 * @deprecated Use Themed/Text variant="h1" instead
 * Legacy title component with uppercase text and normal weight
 */
export const Title = styled.h1`
  font-family: ${props => props.theme.secondaryFont};
  font-weight: normal;
  text-transform: uppercase;
  ${sharedStyles};
`;

Title.defaultProps = {
  fontSize: 4,
};

/**
 * @deprecated Use Themed/Text variant="h1" instead
 * Legacy jumbo heading with uppercase text and normal weight
 */
export const Jumbo = styled.h1`
  font-family: ${props => props.theme.secondaryFont};
  font-weight: normal;
  text-transform: uppercase;
  ${sharedStyles};
`;

Jumbo.defaultProps = {
  fontSize: 7,
};

/**
 * @deprecated Use Themed/Text variant="h1" instead
 * Legacy display heading with bold weight
 */
export const Display = styled.h1`
  font-weight: bold;
  ${sharedStyles};
`;

Display.defaultProps = {
  fontSize: 6,
};

/**
 * @deprecated Use Themed/Text variant="h2" instead
 * Legacy headline component with bold weight
 */
export const Headline = styled.h2`
  font-weight: bold;
  ${sharedStyles};
`;

Headline.defaultProps = {
  fontSize: 5,
};

/**
 * @deprecated Use Themed/Text variant="h3" instead
 * Legacy subheader component with bold weight
 */
export const Subheader = styled.h3`
  font-weight: bold;
  ${sharedStyles};
`;

Subheader.defaultProps = {
  fontSize: 3,
};

/**
 * @deprecated Use Themed/Text variant="p1" instead
 * Legacy body text component with normal weight
 */
export const Body = styled.p`
  font-weight: normal;
  ${sharedStyles};
`;

Body.defaultProps = {
  fontSize: 3,
};

/**
 * @deprecated Use Themed/Text variant="caption" instead
 * Legacy caption component with normal weight
 */
export const Caption = styled.p`
  font-weight: normal;
  ${sharedStyles};
`;

Caption.defaultProps = {
  fontSize: 2,
};

/**
 * @deprecated Use Themed/Text variant="p3" instead
 * Legacy small text component with normal weight
 */
export const Small = styled.small`
  font-weight: normal;
  ${sharedStyles};
`;

Small.defaultProps = {
  fontSize: 0,
};

/**
 * @deprecated Use Themed/Text variant="button" instead
 * Legacy CTA style with bold weight and letter spacing
 */
const ctaStyle = css`
  font-weight: bold;
  letter-spacing: 2px;
  ${sharedStyles};
`;

/**
 * @deprecated Use Themed/Text variant="smallButton" instead
 * Legacy small CTA style
 */
export const smallCtaStyle = css`
  font-size: ${props => props.theme.fontSizes[1]};
  ${ctaStyle}
  ${sharedStyles};
`;

/**
 * @deprecated Use Themed/Text variant="button" instead
 * Legacy medium CTA style
 */
export const mediumCtaStyle = css`
  font-size: ${props => props.theme.fontSizes[2]};
  ${ctaStyle}
  ${sharedStyles};
`;

/**
 * @deprecated Use Themed/Text variant="button" instead
 * Legacy large CTA style
 */
export const largeCtaStyle = css`
  font-size: ${props => props.theme.fontSizes[3]};
  ${ctaStyle}
  ${sharedStyles};
`;

// DESIGN SYSTEM

/**
 * H1 heading component with bold weight
 * @deprecated Use Themed/Text variant="h1" instead
 */
export const H1 = styled(Text).attrs(props => ({
  as: "h1",
  fontSize: FONT_SIZES.H1,
  fontWeight: FONT_WEIGHTS.BOLD,
  ...props,
}))``;

/**
 * H1 heading component with semi-bold weight
 * @deprecated Use Themed/Text variant="h1semi" instead
 */
export const H1Semi = styled(Text).attrs(props => ({
  as: "h1",
  fontSize: FONT_SIZES.H1,
  fontWeight: FONT_WEIGHTS.SEMI_BOLD,
  ...props,
}))``;

/**
 * H1.5 heading component with bold weight
 * @deprecated Use Themed/Text variant="h1p5" instead
 */
export const H1P5 = styled(Text).attrs(props => ({
  as: "h1",
  fontSize: FONT_SIZES.H1P5,
  fontWeight: FONT_WEIGHTS.BOLD,
  ...props,
}))``;

/**
 * H1.5 heading component with semi-bold weight
 * @deprecated Use Themed/Text variant="h1p5semi" instead
 */
export const H1P5Semi = styled(Text).attrs(props => ({
  as: "h1",
  fontSize: FONT_SIZES.H1P5,
  fontWeight: FONT_WEIGHTS.SEMI_BOLD,
  ...props,
}))``;

/**
 * H2 heading component with bold weight
 * @deprecated Use Themed/Text variant="h2" instead
 */
export const H2 = styled(Text).attrs(props => ({
  as: "h2",
  fontSize: FONT_SIZES.H2,
  fontWeight: FONT_WEIGHTS.BOLD,
  ...props,
}))``;

/**
 * H2 heading component with semi-bold weight
 * @deprecated Use Themed/Text variant="h2semi" instead
 */
export const H2Semi = styled(Text).attrs(props => ({
  as: "h2",
  fontSize: FONT_SIZES.H2,
  fontWeight: FONT_WEIGHTS.SEMI_BOLD,
  ...props,
}))``;

/**
 * H2.5 heading component with bold weight
 * @deprecated Use Themed/Text variant="h2p5" instead
 */
export const H2P5 = styled(Text).attrs(props => ({
  as: "h2",
  fontSize: FONT_SIZES.H2P5,
  fontWeight: FONT_WEIGHTS.BOLD,
  ...props,
}))``;

/**
 * H2.5 heading component with semi-bold weight
 * @deprecated Use Themed/Text variant="h2p5semi" instead
 */
export const H2P5Semi = styled(Text).attrs(props => ({
  as: "h2",
  fontSize: FONT_SIZES.H2P5,
  fontWeight: FONT_WEIGHTS.SEMI_BOLD,
  ...props,
}))``;

/**
 * H3 heading component with bold weight
 * @deprecated Use Themed/Text variant="h3" instead
 */
export const H3 = styled(Text).attrs(props => ({
  as: "h3",
  fontSize: FONT_SIZES.H3,
  fontWeight: FONT_WEIGHTS.BOLD,
  ...props,
}))``;

/**
 * H3 heading component with semi-bold weight
 * @deprecated Use Themed/Text variant="h3semi" instead
 */
export const H3Semi = styled(Text).attrs(props => ({
  as: "h3",
  fontSize: FONT_SIZES.H3,
  fontWeight: FONT_WEIGHTS.SEMI_BOLD,
  ...props,
}))``;

/**
 * H4 heading component with bold weight
 * @deprecated Use Themed/Text variant="h4" instead
 */
export const H4 = styled(Text).attrs(props => ({
  as: "h4",
  fontSize: FONT_SIZES.H4,
  fontWeight: FONT_WEIGHTS.BOLD,
  ...props,
}))``;

/**
 * H4 heading component with semi-bold weight
 * @deprecated Use Themed/Text variant="h4semi" instead
 */
export const H4Semi = styled(Text).attrs(props => ({
  as: "h4",
  fontSize: FONT_SIZES.H4,
  fontWeight: FONT_WEIGHTS.SEMI_BOLD,
  ...props,
}))``;

/**
 * H5 heading component with bold weight and letter spacing
 * @deprecated Use Themed/Text variant="h5" instead
 */
export const H5 = styled(Text).attrs(props => ({
  as: "h5",
  fontSize: FONT_SIZES.H5,
  letterSpacing: LETTER_SPACING.H5,
  fontWeight: FONT_WEIGHTS.BOLD,
  ...props,
}))``;

/**
 * H5 heading component with semi-bold weight and letter spacing
 * @deprecated Use Themed/Text variant="h5semi" instead
 */
export const H5Semi = styled(Text).attrs(props => ({
  as: "h5",
  fontSize: FONT_SIZES.H5,
  letterSpacing: LETTER_SPACING.H5,
  fontWeight: FONT_WEIGHTS.SEMI_BOLD,
  ...props,
}))``;

/**
 * H6 heading component with medium weight
 * @deprecated Use Themed/Text variant="h6" instead
 */
export const H6 = styled(Text).attrs(props => ({
  as: "h6",
  fontSize: FONT_SIZES.H6,
  fontWeight: FONT_WEIGHTS.MEDIUM,
  ...props,
}))``;

/**
 * H6 heading component with semi-bold weight
 * @deprecated Use Themed/Text variant="h6semi" instead
 */
export const H6Semi = styled(Text).attrs(props => ({
  as: "h6",
  fontSize: FONT_SIZES.H6,
  fontWeight: FONT_WEIGHTS.SEMI_BOLD,
  ...props,
}))``;

/**
 * P1 text component with regular weight
 * @deprecated Use Themed/Text variant="p1" instead
 */
export const P1 = styled(Text).attrs(props => ({
  as: "p",
  fontSize: FONT_SIZES.P1,
  fontWeight: FONT_WEIGHTS.REGULAR,
  ...props,
}))``;

/**
 * P1 text component with bold weight
 * @deprecated Use Themed/Text variant="p1bold" instead
 */
export const P1Bold = styled(Text).attrs(props => ({
  as: "p",
  fontSize: FONT_SIZES.P1,
  fontWeight: FONT_WEIGHTS.BOLD,
  ...props,
}))``;

/**
 * P2 text component with regular weight
 * @deprecated Use Themed/Text variant="p2" instead
 */
export const P2 = styled(Text).attrs(props => ({
  as: "p",
  fontSize: FONT_SIZES.P2,
  fontWeight: FONT_WEIGHTS.REGULAR,
  ...props,
}))``;

/**
 * P2 text component with bold weight
 * @deprecated Use Themed/Text variant="p2bold" instead
 */
export const P2Bold = styled(Text).attrs(props => ({
  as: "p",
  fontSize: FONT_SIZES.P2,
  fontWeight: FONT_WEIGHTS.BOLD,
  ...props,
}))``;

/**
 * P3 text component with regular weight and letter spacing
 * @deprecated Use Themed/Text variant="p3" instead
 */
export const P3 = styled(Text).attrs(props => ({
  as: "p",
  fontSize: FONT_SIZES.P3,
  letterSpacing: LETTER_SPACING.P3,
  fontWeight: FONT_WEIGHTS.REGULAR,
  ...props,
}))``;

/**
 * P3 text component with bold weight and letter spacing
 * @deprecated Use Themed/Text variant="p3bold" instead
 */
export const P3Bold = styled(Text).attrs(props => ({
  as: "p",
  fontSize: FONT_SIZES.P3,
  letterSpacing: LETTER_SPACING.P3,
  fontWeight: FONT_WEIGHTS.BOLD,
  ...props,
}))``;

/**
 * Body small text component with regular weight
 * @deprecated Use Themed/Text variant="bodySmall" instead
 */
export const BodySmall = styled(Text).attrs(props => ({
  as: "p",
  fontSize: FONT_SIZES.BODY_SMALL,
  fontWeight: FONT_WEIGHTS.REGULAR,
  ...props,
}))``;

/**
 * Caption text component with regular weight
 * @deprecated Use Themed/Text variant="caption" instead
 */
export const TextCaption = styled(Text).attrs(props => ({
  as: "p",
  fontSize: FONT_SIZES.CAPTION,
  fontWeight: FONT_WEIGHTS.REGULAR,
  ...props,
}))``;

/**
 * Button text component with bold weight, uppercase and letter spacing
 * @deprecated Use Themed/Text variant="button" instead
 */
export const Button = styled(Text).attrs(props => ({
  as: "span",
  fontSize: FONT_SIZES.BUTTON,
  fontWeight: FONT_WEIGHTS.BOLD,
  textTransform: TEXT_TRANSFORM.UPPERCASE,
  letterSpacing: LETTER_SPACING.BUTTON,
  ...props,
}))``;

/**
 * Small button text component with semi-bold weight and uppercase
 * @deprecated Use Themed/Text variant="smallButton" instead
 */
export const SmallButton = styled(Text).attrs(props => ({
  as: "span",
  fontSize: FONT_SIZES.SMALL_BUTTON,
  fontWeight: FONT_WEIGHTS.SEMI_BOLD,
  textTransform: TEXT_TRANSFORM.UPPERCASE,
  ...props,
}))``;

/**
 * H7 heading component with bold weight, uppercase and custom letter spacing
 * @deprecated Use Themed/Text variant="h7" instead
 */
export const H7 = styled(Text).attrs({
  as: "p",
  fontWeight: "bold",
  textTransform: "uppercase",
  fontSize: "9px",
  lineHeight: "15px",
  letterSpacing: "0.1em",
})``;

/**
 * Base paragraph styling object
 * @deprecated Use Themed/Text variant="p1" or "p2" instead
 */
export const pStyling = {
  fontSize: "14px",
  letterSpacing: "0.04em",
  lineHeight: "160%",
};

/**
 * Base paragraph component with default styling
 * @deprecated Use Themed/Text variant="p1" instead
 */
export const P = styled(Text).attrs(props => ({
  as: "p",
  ...pStyling,
  ...props,
}))``;

/**
 * P1 link component with underline decoration
 * @deprecated Use Themed/Text variant="p1" as="a" instead
 */
export const P1Link = styled(P1).attrs({
  as: "a",
  textDecoration: "underline",
})``;

P1Link.defaultProps = {
  color: "white",
};

/**
 * P2 link component with underline decoration
 * @deprecated Use Themed/Text variant="p2" as="a" instead
 */
export const P2Link = styled(P2).attrs({
  as: "a",
  textDecoration: "underline",
})``;

P2Link.defaultProps = {
  color: "white",
};

/**
 * Base link component with underline decoration
 * @deprecated Use Themed/Text variant="p1" as="a" instead
 */
export const A = styled(Text).attrs({
  as: "a",
  fontSize: "14px",
  letterSpacing: "0.04em",
  lineHeight: "160%",
  textDecoration: "underline",
})``;

/**
 * Text component with line clamping support
 * @deprecated Use Themed/Text with lineClamp prop instead
 * Example: <Text variant="p1" lineClamp={2}>
 */
export const LimitLineText = styled(Text)`
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: ${({ lineClamp }) => lineClamp || 1};
  -webkit-box-orient: vertical;
`;
